/* eslint-disable no-nested-ternary */
import React, { FC, memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useRouter } from 'next/router'
import { authSelectors } from '@ucheba/store/auth'
import { Heading } from '@ucheba/ui/components/Heading/desktop'
import { EInstitutionType } from '@ucheba/store/institutions/item/types'
import { EActivityTypes } from '@ucheba/utils/types/userActivityTypes'
import { Spacing } from '@ucheba/ui/components/Spacing/desktop'
import { Button } from '@ucheba/ui/components/Button/desktop'
import OpendLockIcon from '@ucheba/ui/icons/icon__opened-lock.svg'
import {
  getFullRouterPath,
  objectToQueryString,
  redirect,
} from '@ucheba/utils/helpers/router'
import { coreQuery } from '@ucheba/utils/constants/core'
import Item from './InstitutionRatesItem'

import {
  dictionary,
  FIELDS_WITH_ROUNDED_VALUES,
  institutionTypeNameLabels,
} from '../constants'

import { IRatesProps } from '../types'

import SC from '../base/InstitutionRatesItem.styles'
import { useUserActivity } from '../../../../utils/hooks/useUserActivity'
import { EActivityInfoPart } from '../../InstitutionExams/types'

const { Block } = SC

const InstitutionRates: FC<IRatesProps> = ({
  type,
  currentSummary,
  averageSummary,
  maximumSummary,
}) => {
  const isAuth = useSelector(authSelectors.isAuth)
  const router = useRouter()
  const { asPath: currentUrl, query } = router
  const [colapsed, setColapsed] = useState(false)

  const { id: institutionId } = query

  const { setUserActivity } = useUserActivity()

  const handleSetActivity = async (): Promise<void> => {
    await setUserActivity({
      activityType: EActivityTypes.infoPartViewed,
      institutionId: Array.isArray(institutionId)
        ? Number(institutionId[0])
        : Number(institutionId),
      data: { infoPart: EActivityInfoPart.indicators },
    })
  }

  const handleShowAllClick = useCallback((): void => {
    if (isAuth) {
      setColapsed(true)

      handleSetActivity()

      return
    }

    redirect({
      location: getFullRouterPath(
        '/auth',
        objectToQueryString({ [coreQuery.redirect]: `${currentUrl}#indicators` })
      ),
    })
  }, [currentUrl, isAuth])

  // если есть хэш отправляем активность и удаляем хэш
  useEffect(() => {
    if (isAuth && window.location.hash === '#indicators') {
      handleSetActivity()

      setColapsed(true)

      router.replace(router.asPath.replace(/#\w+/g, ''))
    }
  }, [])

  const items = useMemo(() => {
    return Object.keys(dictionary.rates).filter((key) => {
      if (key === 'averagePrice' && type === EInstitutionType.University) return false
      return true
    })
  }, [type])

  const filteredItems = useMemo(
    () =>
      items.filter((key) => {
        const current = FIELDS_WITH_ROUNDED_VALUES.includes(key)
          ? Math.round(currentSummary[key] / 1000) * 1000
          : currentSummary[key]

        const average = FIELDS_WITH_ROUNDED_VALUES.includes(key)
          ? Math.round(averageSummary[key] / 1000) * 1000
          : averageSummary[key]

        const maximum = maximumSummary[key]

        const isDataCorrect =
          Number.isFinite(current) &&
          Number.isFinite(average) &&
          Number.isFinite(maximum) &&
          maximum > 0

        return isDataCorrect && current
      }),
    [colapsed, items, averageSummary, maximumSummary, currentSummary]
  )

  return (
    <div>
      <Heading h2={`Ключевые показатели ${dictionary.title[type]}`} spacing='xlarge' />

      <Block>
        {filteredItems.slice(0, colapsed ? 9 : 4).map((key) => {
          const current = FIELDS_WITH_ROUNDED_VALUES.includes(key)
            ? Math.round(currentSummary[key] / 1000) * 1000
            : currentSummary[key]

          const average = FIELDS_WITH_ROUNDED_VALUES.includes(key)
            ? Math.round(averageSummary[key] / 1000) * 1000
            : averageSummary[key]

          const maximum = maximumSummary[key]

          const subtitle =
            key === 'budgetPlacesCount'
              ? institutionTypeNameLabels[type].allIn
              : key === 'countStudents' && type === EInstitutionType.College
                ? dictionary.rates[key].subtitleCollege
                : dictionary.rates[key].subtitle

          const isDataCorrect =
            Number.isFinite(current) &&
            Number.isFinite(average) &&
            Number.isFinite(maximum) &&
            maximum > 0

          if (!isDataCorrect || !current) {
            return null
          }

          return (
            <Item
              key={key}
              institutionLabel={institutionTypeNameLabels[type].general}
              title={dictionary.rates[key].title}
              subtitle={subtitle}
              current={current}
              postfix={dictionary.rates[key]?.postfix}
              average={average}
              maximum={maximum}
              isLocked={!isAuth}
              onLockIconClick={handleShowAllClick}
            />
          )
        })}
      </Block>

      {!colapsed && filteredItems.length > 4 && (
        <>
          <Spacing spacing='xlarge' />

          <Button
            block
            view='outline'
            color='blue'
            StartIcon={isAuth ? null : <OpendLockIcon width='24px' height='24px' />}
            onClick={handleShowAllClick}
          >
            Открыть все показатели
          </Button>
        </>
      )}
    </div>
  )
}

export default memo(InstitutionRates)
